import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot } from 'lucide-react';
import ChatInterface from './ChatInterface';

const NavChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);

  // Stop pulsing after first interaction
  useEffect(() => {
    if (isOpen) {
      setIsPulsing(false);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Check if the click is on the overlay (semi-transparent background)
      const isOverlay = target.classList.contains('chat-overlay');
      
      // Check if click is on the chat button
      const isChatButton = target.closest('.chat-button');
      
      // Only close if clicking directly on the overlay
      if (isOverlay && !isChatButton) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle click on the chat button
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState) {
      setIsPulsing(false);
    }
  };

  return (
    <div className="relative">
      {/* Chat Interface */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 bg-black/50 z-[9998] chat-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Chat Container */}
            <motion.div
              className="fixed inset-0 z-[9999] flex items-start justify-center p-0 md:p-4 pointer-events-none"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300, mass: 0.5 }}
            >
              <div className="w-full h-full md:w-full md:max-w-lg md:h-[85vh] md:max-h-[800px] bg-white md:rounded-xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto chat-container">
                <ChatInterface onClose={() => setIsOpen(false)} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Chat Button */}
      <button
        className={`relative flex items-center justify-center w-auto px-3 h-10 text-sm font-medium rounded-full group chat-button ${
          isOpen 
            ? 'fixed right-4 bottom-4 sm:static bg-orange-100 text-orange-700' 
            : 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
        } transition-all duration-200 shadow-lg sm:shadow-none`}
        aria-label={isOpen ? 'Close chat' : 'Chat with Renaissance AI'}
        onClick={handleButtonClick}
      >
        <div className="relative flex items-center justify-center">
          <Bot className="w-5 h-5" />
          <span className="ml-2 font-medium sm:hidden">Renaissance AI</span>
          <span className="hidden ml-2 sm:inline">Ask AI</span>
          {isPulsing && !isOpen && (
            <motion.span
              className="absolute inset-0 rounded-full bg-orange-500/20"
              initial={{ scale: 1, opacity: 0.7 }}
              animate={{ scale: 1.8, opacity: 0 }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeOut',
              }}
              style={{
                top: '-0.5rem',
                left: '-0.5rem',
                right: '-0.5rem',
                bottom: '-0.5rem',
              }}
              aria-hidden="true"
            />
          )}
        </div>
      </button>
    </div>
  );
};

export default NavChatButton;
